import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  acceptInvitationResultSchema,
  invitationPreviewSchema,
  type InvitationPreview,
} from '@forecastify/shared';
import { Button } from '@forecastify/ui';
import { apiUrl } from '../../api';
import { AuthFrame } from '../auth/auth-frame';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { InviteProfile } from './invite-profile';
import { claimMessage, outcomeMessage } from './invite-messages';

export { claimMessage, outcomeMessage };

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `/invite/:token` — the other end of an invitation. C17.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS PAGE RENDERS FOR SOMEBODY WITH NO ACCOUNT, so it is `chrome: 'bare'` and it fetches
 * WITHOUT a session.** Every other screen lives inside `AppShell`, which needs a workspace and a
 * membership — the whole point of this one is that the visitor has neither. The preview goes through
 * `fetch` directly rather than `session.request`, because that helper attaches a token and a
 * workspace header that do not exist yet.
 *
 * ⚠️ **Four states, and each one gets a sentence.** A link that is expired, revoked, already used or
 * for a different account is not "an error" — it is a specific thing that happened, and a page that
 * says "something went wrong" leaves somebody emailing an admin to find out which.
 */

export function InviteScreen() {
  const t = useT();
  const session = useSession();
  const navigate = useNavigate();
  const { token = '' } = useParams<{ token: string }>();

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    /* ⚠️ Plain `fetch`: no bearer token, no `x-workspace-id`. The endpoint is `@Public()` and the
     * token in the URL is the whole authorisation. */
    const response = await fetch(apiUrl(`/invitations/${encodeURIComponent(token)}`)).catch(
      () => null,
    );
    if (response?.ok !== true) {
      setMissing(true);
      return;
    }
    const parsed = invitationPreviewSchema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) {
      setMissing(true);
      return;
    }
    setPreview(parsed.data);
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Accept, and go in.
   *
   * ⚠️ Split out so both paths use it: somebody who already had an account, and somebody who has
   * just set a password. The server re-checks the token and the address either way — there is one
   * way in, however you arrived.
   */
  const acceptNow = async (): Promise<void> => {
    /* ⚠️ Through `session.request` — accepting needs the identity, and this helper is what carries
     * it. The workspace header it adds is ignored by the endpoint. */
    const response = await session
      .request(`/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST' })
      .catch(() => null);

    if (response?.ok !== true) {
      setFailure(t('error.unavailable'));
      return;
    }
    const parsed = acceptInvitationResultSchema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) {
      setFailure(t('error.unavailable'));
      return;
    }
    if (parsed.data.outcome === 'joined' || parsed.data.outcome === 'already_member') {
      /**
       * ⚠️⚠️ **THE SESSION IS RELOADED BEFORE NAVIGATING, because the workspace list is what the app
       * routes on.** `/auth/me` was answered before this membership existed, so the shell is still
       * holding a list that does not contain the workspace somebody just joined — and going straight
       * to `/` would land them on "no workspace".
       */
      await session.reload();
      void navigate('/');
      return;
    }
    setFailure(outcomeMessage(t, parsed.data.outcome));
  };

  const accept = (): void => {
    void (async () => {
      setBusy(true);
      setFailure(null);
      await acceptNow();
      setBusy(false);
    })();
  };

  /**
   * Set a password, sign in, join — one button.
   *
   * ⚠️⚠️ **THREE REQUESTS BEHIND ONE CLICK, and the alternative was five steps with two mailbox
   * visits.** There is no sign-up screen in this app, and `enable_confirmations = true` means an
   * ordinary sign-up would mail a second link to an address the invitation has already proved. So
   * the server creates the account **pre-confirmed** on the strength of the token (see
   * `gotrue.ts:createConfirmedUser`), this signs in with the password just set, and then accepts.
   *
   * ⚠️ **Each step's failure says which step failed.** One "something went wrong" over a
   * three-request sequence is the message that makes somebody email an admin — and the middle one
   * matters most: an account that exists with a password that works, but which did not get in.
   */
  if (missing) {
    return (
      <Frame>
        <p className="text-[13px]">{t('invite.accept.unknown')}</p>
        <Button
          variant="outline"
          onClick={() => {
            void navigate('/sign-in');
          }}
        >
          {t('invite.accept.signIn')}
        </Button>
      </Frame>
    );
  }

  if (preview === null) {
    /* ⚠️ The loading state says what it is waiting for. `state-rules.md` §1. */
    return (
      <Frame>
        <p className="text-muted-foreground text-[12.5px]">{t('state.loading')}</p>
      </Frame>
    );
  }

  const unusable = preview.state !== 'pending';

  /**
   * ⚠️⚠️ `0202` · **NO ACCOUNT AND A LIVE LINK: SETUP STEP 1, IN THE SETUP FLOW'S OWN FRAME.** The
   * name, a picture and the password, with the preview beside them — `invite-profile.tsx`. This
   * page's frame stays for everything else: loading, a dead link, and somebody already signed in.
   */
  if (!unusable && session.status !== 'signed-in') {
    return <InviteProfile preview={preview} token={token} />;
  }

  return (
    <Frame>
      <p className="text-[13px] leading-relaxed">
        {preview.inviterName === null
          ? t('invite.accept.leadNoInviter', { workspace: preview.workspaceName })
          : t('invite.accept.lead', {
              inviter: preview.inviterName,
              workspace: preview.workspaceName,
            })}
      </p>
      {/* ⚠️ `null` for an organisation invitation (`0202`): its admin owns what they create. */}
      {preview.roleName === null ? null : (
        <p className="text-muted-foreground text-[12.5px]">
          {t('invite.accept.role', { role: preview.roleName })}
        </p>
      )}

      {/**
       * ⚠️⚠️ **THE INVITED ADDRESS IS ON THE PAGE, and it is not decoration.** Acceptance is refused
       * unless the signed-in account's own address is this one — so somebody who signed up with a
       * different address has to be told which one the invitation is for, or the refusal is a
       * mystery they cannot act on.
       */}
      <p className="bg-muted/50 rounded-md px-3 py-2 text-[12px]">{preview.email}</p>

      {unusable ? (
        <p role="alert" className="text-[12.5px]">
          {preview.state === 'expired'
            ? t('invite.accept.expired')
            : preview.state === 'revoked'
              ? t('invite.accept.revoked')
              : t('invite.accept.used')}
        </p>
      ) : session.status === 'signed-in' ? (
        <>
          {failure !== null && (
            <p role="alert" className="text-destructive text-[12.5px]">
              {failure}
            </p>
          )}
          <Button disabled={busy} onClick={accept}>
            {t('invite.accept.join')}
          </Button>
        </>
      ) : /* ⚠️ Unreachable — the signed-out, pending case returned `InviteProfile` above. Kept as
           the type's exhaustive end rather than as a second form. */
      null}
    </Frame>
  );
}

/**
 * The card.
 *
 * ⚠️⚠️ **`AuthFrame` NOW, WHERE THIS DREW ITS OWN.** Until C18 this page had a 420px card, a
 * violet icon tile and a 14px heading, while the sign-in screen twenty metres away had a 360px card
 * with neither — two designs for the same moment in the same product, arrived at independently
 * because neither had a mockup to answer to. The belépés mockup settles it for all four signed-out
 * screens at 392px with the logo above, and CLAUDE.md §3 is the general rule: *"screens compose
 * components; they do not define them."*
 *
 * ⚠️ **The icon tile is gone rather than moved.** The mockup's card has no ornament at all above
 * its title, and the logo it puts over the card is a stronger version of the same signal.
 */
function Frame({ children }: { readonly children: React.ReactNode }) {
  const t = useT();
  return (
    <AuthFrame screen="invite" title={t('invite.accept.title')}>
      {children}
    </AuthFrame>
  );
}
